import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { applyFees, toMinorUnits } from "./pricing";

// ----- Helper: list pending offers for the signed-in Helper -----
export const listMyOffers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("job_offers")
      .select(
        `id, status, score, expires_at, created_at,
         job:jobs(id, title, description, postcode, urgency, scheduled_at, budget_min, budget_max, category_id)`,
      )
      .eq("helper_id", userId)
      .in("status", ["pending", "accepted"])
      .order("created_at", { ascending: false })
      .limit(25);
    if (error) throw new Error(error.message);
    return { offers: data ?? [] };
  });

// ----- Helper bookings with payout status (for helper dashboard) -----
export const listMyHelperBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("bookings")
      .select(`id, status, payment_status, payout_status, payout_amount, payout_completed_at,
               payout_failure_reason, helper_payout, total_amount, scheduled_start, created_at,
               job:jobs(id, title, postcode)`)
      .eq("helper_id", userId)
      .order("created_at", { ascending: false })
      .limit(30);
    if (error) throw new Error(error.message);
    return { bookings: data ?? [] };
  });

// ----- Helper accepts an offer (first-accept-wins) -----
export const acceptOffer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ offerId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: result, error } = await supabase.rpc("accept_job_offer", { _offer_id: data.offerId });
    if (error) throw new Error(error.message);
    const row = Array.isArray(result) ? result[0] : result;
    if (row?.booking_id && row?.status === "accepted") {
      const { sendBookingEmail } = await import("./email.server");
      await sendBookingEmail(row.booking_id, "offer_accepted_customer").catch(() => {});
    }
    return { bookingId: row?.booking_id ?? null, status: row?.status ?? "unknown" };
  });

export const declineOffer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ offerId: z.string().uuid(), reason: z.string().max(280).optional() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.rpc("decline_job_offer", {
      _offer_id: data.offerId,
      _reason: data.reason ?? undefined,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ----- Customer fetches their booking (for the confirm page) -----
export const getBookingForCheckout = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ bookingId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: booking, error } = await supabase
      .from("bookings")
      .select(
        `id, status, payment_status, agreed_rate, estimated_duration_minutes, subtotal,
         helper_fee, customer_fee, total_amount, helper_payout, currency, scheduled_start,
         referral_credit_applied,
         job:jobs(id, title, description, postcode),
         helper:profiles!bookings_helper_id_fkey(id, full_name, avatar_url)`,
      )
      .eq("id", data.bookingId)
      .eq("customer_id", userId)
      .single();
    if (error || !booking) throw new Error("booking_not_found");
    return { booking };
  });

// ----- Customer creates a Stripe Checkout session for a booking -----
// Idempotency: passes a stable idempotency key derived from the booking id +
// total + currency to Stripe, so retries (network blips, double-click) reuse
// the same session instead of duplicate-charging.
export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ bookingId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: booking, error } = await supabase
      .from("bookings")
      .select("id, helper_id, total_amount, customer_fee, currency, payment_status, status, job_id, stripe_session_id, job:jobs(title)")
      .eq("id", data.bookingId)
      .eq("customer_id", userId)
      .single();
    if (error || !booking) throw new Error("booking_not_found");
    if (booking.payment_status === "paid") throw new Error("already_paid");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: helper } = await supabaseAdmin
      .from("helper_profiles")
      .select("stripe_account_id, stripe_onboarding_complete")
      .eq("id", booking.helper_id)
      .single();

    const { getStripe, getAppUrl } = await import("./stripe.server");
    const { recordAudit } = await import("./audit.server");
    const { applyBookingTransition } = await import("./booking-states");
    const stripe = getStripe();
    const appUrl = getAppUrl();

    // If we already have an open Stripe session for this booking and the
    // amount hasn't changed, reuse the URL instead of creating a new one.
    if (booking.stripe_session_id) {
      try {
        const existing = await stripe.checkout.sessions.retrieve(booking.stripe_session_id);
        if (existing.status === "open" && existing.amount_total === toMinorUnits(Number(booking.total_amount)) && existing.url) {
          return { url: existing.url, reused: true };
        }
      } catch {
        // ignore — we'll create a fresh one below
      }
    }

    const sessionParams: import("stripe").Stripe.Checkout.SessionCreateParams = {
      mode: "payment",
      // Card covers Apple Pay & Google Pay automatically on supported
      // devices through Stripe Checkout's payment request button.
      payment_method_types: ["card"],
      success_url: `${appUrl}/dashboard?paid=1&booking=${booking.id}`,
      cancel_url: `${appUrl}/jobs/${booking.job_id}/confirm`,
      client_reference_id: booking.id,
      metadata: { booking_id: booking.id, customer_id: userId, helper_id: booking.helper_id },
      line_items: [
        {
          quantity: 1,
          price_data: {
            currency: (booking.currency ?? "GBP").toLowerCase(),
            unit_amount: toMinorUnits(Number(booking.total_amount)),
            product_data: {
              name: ((booking.job as unknown as { title?: string })?.title) ?? "Peopels booking",
            },
          },
        },
      ],
    };

    if (helper?.stripe_account_id && helper.stripe_onboarding_complete) {
      const appFee = toMinorUnits(Number(booking.customer_fee)) + Math.round(toMinorUnits(Number(booking.total_amount)) * 0.10);
      sessionParams.payment_intent_data = {
        application_fee_amount: appFee,
        transfer_data: { destination: helper.stripe_account_id },
        metadata: sessionParams.metadata,
      };
    }

    // Stripe idempotency key — same booking + same amount + same currency
    // collapses retries to a single session.
    const idempotencyKey = `checkout:${booking.id}:${toMinorUnits(Number(booking.total_amount))}:${(booking.currency ?? "GBP").toLowerCase()}`;
    const session = await stripe.checkout.sessions.create(sessionParams, { idempotencyKey });

    await applyBookingTransition({
      supabaseAdmin,
      bookingId: booking.id,
      to: {
        payment_status: "processing",
        extra: {
          stripe_session_id: session.id,
          payment_intent_id: typeof session.payment_intent === "string" ? session.payment_intent : null,
        },
      },
      reason: "checkout_session_created",
      source: "createCheckoutSession",
      actorId: userId,
    });

    await recordAudit({
      actorId: userId,
      action: "checkout.session.created",
      entityType: "booking",
      entityId: booking.id,
      after: { session_id: session.id, total: booking.total_amount, idempotency_key: idempotencyKey },
    });

    return { url: session.url, reused: false };
  });

// ----- Confirm a booking was paid (used by dashboard after Stripe redirect) -----
// Reads current status. If the webhook hasn't landed yet, polls the Stripe
// session (idempotent) and applies the transition so the UI never lies about
// payment state on the success redirect.
export const confirmBookingPaid = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ bookingId: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: booking, error } = await supabase
      .from("bookings")
      .select("id, status, payment_status, stripe_session_id, total_amount, currency")
      .eq("id", data.bookingId)
      .eq("customer_id", userId)
      .single();
    if (error || !booking) throw new Error("booking_not_found");

    if (booking.payment_status === "paid") {
      return { paid: true, status: booking.payment_status, bookingId: booking.id };
    }

    // Webhook may not have arrived yet — verify with Stripe directly.
    if (booking.stripe_session_id) {
      try {
        const { getStripe } = await import("./stripe.server");
        const stripe = getStripe();
        const session = await stripe.checkout.sessions.retrieve(booking.stripe_session_id);
        if (session.payment_status === "paid") {
          const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
          const { applyBookingTransition } = await import("./booking-states");
          const piId = typeof session.payment_intent === "string" ? session.payment_intent : null;
          await applyBookingTransition({
            supabaseAdmin,
            bookingId: booking.id,
            to: { status: "accepted", payment_status: "paid", extra: piId ? { payment_intent_id: piId } : {} },
            reason: "confirmBookingPaid.stripe_verified",
            source: "dashboard_redirect",
            actorId: userId,
          });
          return { paid: true, status: "paid", bookingId: booking.id };
        }
      } catch {
        // ignore — fall through and report current DB state
      }
    }
    return { paid: false, status: booking.payment_status, bookingId: booking.id };
  });




// ----- Booking lifecycle: helper marks started / completed -----
const lifecycleSchema = z.object({ bookingId: z.string().uuid() });

export const markBookingStarted = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => lifecycleSchema.parse(i))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("bookings")
      .update({ status: "in_progress", actual_start: new Date().toISOString() })
      .eq("id", data.bookingId)
      .eq("helper_id", context.userId);
    if (error) throw new Error(error.message);
    const { sendBookingEmail } = await import("./email.server");
    await sendBookingEmail(data.bookingId, "booking_started_customer").catch(() => {});
    await sendBookingEmail(data.bookingId, "booking_started_helper").catch(() => {});
    return { ok: true };
  });

export const markBookingCompleted = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => lifecycleSchema.parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    // Helper marks the job complete. Payout becomes eligible after a 48h
    // customer-confirmation window — customer can confirm instantly to release
    // sooner. We store the eligibility timestamp so the payout cron can pick
    // it up automatically and so the UI can show "Releases on …".

    // Require at least one post-job photo as dispute evidence.
    const { data: pre } = await supabase
      .from("bookings")
      .select("post_job_photos")
      .eq("id", data.bookingId)
      .eq("helper_id", userId)
      .maybeSingle();
    if (!pre) throw new Error("not_found");
    if (!Array.isArray(pre.post_job_photos) || pre.post_job_photos.length === 0) {
      throw new Error("Add at least one finished-job photo before marking the booking complete.");
    }

    const autoReleaseAt = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString();
    const { data: booking, error } = await supabase
      .from("bookings")
      .update({
        status: "completed",
        actual_end: new Date().toISOString(),
        payout_eligible_at: autoReleaseAt,
      })
      .eq("id", data.bookingId)
      .eq("helper_id", userId)
      .select("id, job_id, customer_id, helper_payout, payout_eligible_at")
      .single();
    if (error || !booking) throw new Error(error?.message ?? "not_found");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { recordAudit } = await import("./audit.server");

    if (booking.job_id) {
      await supabaseAdmin.from("jobs").update({ status: "completed" }).eq("id", booking.job_id);
    }
    await supabaseAdmin.from("notifications").insert({
      user_id: booking.customer_id,
      type: "booking_completed",
      title: "Your Helper marked the job complete",
      body: "Confirm completion to release payment instantly, or it will auto-release in 48 hours.",
      link: `/bookings/${booking.id}`,
      data: { booking_id: booking.id, payout_eligible_at: booking.payout_eligible_at },
    });
    await recordAudit({
      actorId: userId,
      action: "booking.completed",
      entityType: "booking",
      entityId: booking.id,
      after: { payout_eligible_at: booking.payout_eligible_at },
    });

    const { sendBookingEmail } = await import("./email.server");
    await sendBookingEmail(booking.id, "booking_completed_customer").catch(() => {});
    await sendBookingEmail(booking.id, "booking_completed_helper").catch(() => {});

    return { ok: true, payoutEligibleAt: booking.payout_eligible_at };
  });

// Customer explicitly confirms the job was completed satisfactorily, which
// makes the payout eligible for release immediately (bypassing the 48h hold).
export const customerConfirmCompletion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => lifecycleSchema.parse(i))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: result, error } = await supabase.rpc("confirm_booking_completion", {
      _booking_id: data.bookingId,
    });
    if (error) throw new Error(error.message);
    return result as { confirmed?: boolean; already_confirmed?: boolean; payout_eligible_at?: string };
  });

// ----- Reviews -----
const reviewSchema = z.object({
  bookingId: z.string().uuid(),
  rating: z.number().int().min(1).max(5),
  comment: z.string().trim().max(1500).optional(),
});

export const submitReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => reviewSchema.parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: booking, error } = await supabase
      .from("bookings")
      .select("id, customer_id, helper_id, status")
      .eq("id", data.bookingId)
      .single();
    if (error || !booking) throw new Error("booking_not_found");
    if (booking.status !== "completed") throw new Error("booking_not_completed");

    const isCustomer = booking.customer_id === userId;
    const isHelper = booking.helper_id === userId;
    if (!isCustomer && !isHelper) throw new Error("forbidden");

    const reviewee_id = isCustomer ? booking.helper_id : booking.customer_id;
    const reviewer_type = isCustomer ? "customer" : "helper";

    const { error: insertErr } = await supabase.from("reviews").insert({
      booking_id: booking.id,
      reviewer_id: userId,
      reviewee_id,
      reviewer_type,
      rating: data.rating,
      comment: data.comment ?? null,
    });
    if (insertErr) throw new Error(insertErr.message);
    const { sendBookingEmail } = await import("./email.server");
    const tpl = isCustomer ? "review_posted_helper" : "review_posted_customer";
    await sendBookingEmail(booking.id, tpl, { rating: data.rating, comment: data.comment ?? "" }).catch(() => {});
    return { ok: true };
  });

// ----- Disputes -----
const disputeSchema = z.object({
  bookingId: z.string().uuid(),
  reason: z.string().trim().min(4).max(120),
  description: z.string().trim().max(4000).optional(),
});

export const raiseDispute = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => disputeSchema.parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: booking, error: bErr } = await supabase
      .from("bookings")
      .select("id, customer_id, helper_id, status")
      .eq("id", data.bookingId)
      .single();
    if (bErr || !booking) throw new Error("booking_not_found");
    if (booking.customer_id !== userId && booking.helper_id !== userId) throw new Error("forbidden");

    const { error: insertErr } = await supabase.from("disputes").insert({
      booking_id: booking.id,
      raised_by: userId,
      reason: data.reason,
      description: data.description ?? null,
    });
    if (insertErr) throw new Error(insertErr.message);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { recordAudit } = await import("./audit.server");
    await supabaseAdmin.from("bookings").update({ status: "disputed" }).eq("id", booking.id);
    await recordAudit({
      actorId: userId,
      action: "dispute.raised",
      entityType: "booking",
      entityId: booking.id,
      after: { reason: data.reason },
    });
    const { sendBookingEmail, notifyAdmins } = await import("./email.server");
    await sendBookingEmail(booking.id, "dispute_opened_customer").catch(() => {});
    await sendBookingEmail(booking.id, "dispute_opened_helper").catch(() => {});
    await notifyAdmins("dispute_opened", "New dispute", data.reason, `/admin/disputes`, { booking_id: booking.id }).catch(() => {});
    return { ok: true };
  });

// ----- Admin: list and resolve disputes -----
async function assertAdmin(context: { supabase: any; userId: string }) {
  const { data, error } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
  if (error || !data) throw new Error("forbidden");
}

export const adminListDisputes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("disputes")
      .select(
        `id, status, reason, description, refund_amount, resolution, created_at,
         booking:bookings(id, total_amount, helper_payout, payment_intent_id,
           customer:profiles!bookings_customer_id_fkey(id, full_name, email),
           helper:profiles!bookings_helper_id_fkey(id, full_name, email))`,
      )
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return { disputes: data ?? [] };
  });

const resolveSchema = z.object({
  disputeId: z.string().uuid(),
  resolution: z.enum(["refund_customer", "pay_helper", "split"]),
  refundAmount: z.number().min(0).optional(),
  notes: z.string().max(2000).optional(),
});

export const adminResolveDispute = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => resolveSchema.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { recordAudit } = await import("./audit.server");

    const { data: dispute, error: dErr } = await supabaseAdmin
      .from("disputes")
      .select("id, booking_id, status, booking:bookings(payment_intent_id, total_amount)")
      .eq("id", data.disputeId)
      .single();
    if (dErr || !dispute) throw new Error("dispute_not_found");

    let stripeRefundId: string | null = null;
    if (data.resolution !== "pay_helper" && data.refundAmount && data.refundAmount > 0) {
      try {
        const { getStripe } = await import("./stripe.server");
        const { toMinorUnits } = await import("./pricing");
        const piId = (dispute.booking as unknown as { payment_intent_id?: string })?.payment_intent_id;
        if (piId) {
          const refund = await getStripe().refunds.create({
            payment_intent: piId,
            amount: toMinorUnits(data.refundAmount),
            reverse_transfer: true,
            refund_application_fee: true,
          }, { idempotencyKey: `refund:${dispute.id}:${toMinorUnits(data.refundAmount)}` });
          stripeRefundId = refund.id;
        }
      } catch (err) {
        console.error("[dispute] refund failed", err);
      }
    }


    await supabaseAdmin
      .from("disputes")
      .update({
        status: "resolved",
        resolution: data.resolution,
        refund_amount: data.refundAmount ?? null,
        resolved_by: context.userId,
        resolved_at: new Date().toISOString(),
      })
      .eq("id", dispute.id);

    if (dispute.booking_id) {
      const newBookingStatus = data.resolution === "refund_customer" ? "cancelled" : "completed";
      await supabaseAdmin.from("bookings").update({ status: newBookingStatus }).eq("id", dispute.booking_id);
    }

    await recordAudit({
      actorId: context.userId,
      action: "dispute.resolved",
      entityType: "dispute",
      entityId: dispute.id,
      after: { resolution: data.resolution, refund_amount: data.refundAmount, stripe_refund_id: stripeRefundId },
      notes: data.notes,
    });

    return { ok: true, stripeRefundId };
  });

// ----- Customer dashboard: bookings with helper + payment status -----
export const listMyCustomerBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("bookings")
      .select(`id, status, payment_status, total_amount, scheduled_start, created_at,
               job:jobs(id, title, postcode),
               helper:profiles!bookings_helper_id_fkey(id, full_name, avatar_url)`)
      .eq("customer_id", userId)
      .order("created_at", { ascending: false })
      .limit(30);
    if (error) throw new Error(error.message);
    return { bookings: data ?? [] };
  });

// ----- Helper dashboard: aggregate earnings/performance metrics -----
export const getHelperMetrics = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

    const [{ data: profile }, { data: profileBase }, { data: bookings }, { data: offers }] = await Promise.all([
      supabase.from("helper_profiles")
        .select("avg_rating, rating_count, response_time_minutes, completion_rate, profile_completion_score, headline, about, base_postcode, hourly_rate, id_verified, stripe_onboarding_complete")
        .eq("id", userId).maybeSingle(),
      supabase.from("profiles").select("avatar_url").eq("id", userId).maybeSingle(),
      supabase.from("bookings")
        .select("status, helper_payout, payout_status, created_at, payout_completed_at")
        .eq("helper_id", userId),
      supabase.from("job_offers")
        .select("status, created_at, responded_at")
        .eq("helper_id", userId).gte("created_at", monthStart),
    ]);

    const completed = (bookings ?? []).filter((b) => b.status === "completed");
    const lifetimeEarnings = completed.reduce((s, b) => s + Number(b.helper_payout ?? 0), 0);
    const monthEarnings = completed
      .filter((b) => b.payout_completed_at && b.payout_completed_at >= monthStart)
      .reduce((s, b) => s + Number(b.helper_payout ?? 0), 0);
    const weekEarnings = completed
      .filter((b) => b.payout_completed_at && b.payout_completed_at >= weekStart)
      .reduce((s, b) => s + Number(b.helper_payout ?? 0), 0);
    const pendingPayout = (bookings ?? [])
      .filter((b) => b.payout_status === "pending" || b.payout_status === "in_transit")
      .reduce((s, b) => s + Number(b.helper_payout ?? 0), 0);

    const totalOffers = (offers ?? []).length;
    const acceptedOffers = (offers ?? []).filter((o) => o.status === "accepted").length;
    const responded = (offers ?? []).filter((o) => o.responded_at).length;
    const responseRate = totalOffers ? Math.round((responded / totalOffers) * 100) : 0;
    const acceptRate = totalOffers ? Math.round((acceptedOffers / totalOffers) * 100) : 0;

    // Profile completion checklist
    const checks = [
      { key: "avatar", label: "Profile photo", done: !!profileBase?.avatar_url },
      { key: "headline", label: "Headline", done: !!profile?.headline },
      { key: "bio", label: "About (50+ chars)", done: (profile?.about ?? "").length >= 50 },
      { key: "postcode", label: "Service area", done: !!profile?.base_postcode },
      { key: "rate", label: "Hourly rate", done: !!profile?.hourly_rate },
      { key: "id", label: "ID verified", done: !!profile?.id_verified },
      { key: "stripe", label: "Payouts connected", done: !!profile?.stripe_onboarding_complete },
    ];
    const completion = Math.round((checks.filter((c) => c.done).length / checks.length) * 100);

    return {
      profile: profile ? { ...profile, avatar_url: profileBase?.avatar_url ?? null } : null,
      earnings: {
        week: weekEarnings,
        month: monthEarnings,
        lifetime: lifetimeEarnings,
        pendingPayout,
      },
      performance: {
        completedJobs: completed.length,
        avgRating: Number(profile?.avg_rating ?? 0),
        ratingCount: Number(profile?.rating_count ?? 0),
        responseRate,
        acceptRate,
        responseTimeMinutes: profile?.response_time_minutes ?? null,
      },
      checklist: { items: checks, percent: completion },
    };
  });
